/**
 * src/lib/projectTagWarning.ts
 *
 * Single source of truth for the project-tag advisory gate, shared by the
 * server (server/modules/team/service.ts, gates every non-composer write
 * path — MCP, REST, relayAdapter) and the client (ERComposerV1.tsx, the one
 * write path that bypasses the server entirely by writing to Firestore
 * directly). Before this, each side had its own independently-drifted copy
 * of the required-tag list — the client's only covered the older 7-tag
 * "Sprint 80" convention, missing the newer §34 additions (interview,
 * prd-draft, prd-update, feature-card, acceptance-criteria, test-case,
 * test-result, track-backlog) the server already enforced.
 *
 * Team Log seq #35089 (dev-3 investigation) → the discussion that led here:
 * one single advisory gate (never a hard rejection — a prior narrow
 * hard-blocking gate, keyed on a *different* bare-project-name tag
 * convention, was retired as part of this same change), with a per-author
 * (not pooled) counter of missed-tag posts. At PROJECT_TAG_WARNING_THRESHOLD,
 * the author sees PROJECT_TAG_WARNING_MESSAGE instead of the silent
 * server-log-only behavior below it.
 */

// Charter §34 — every post tagged with one of these MUST carry a
// project:{slug} tag identifying the product the work belongs to.
export const PROJECT_TAG_REQUIRED_TYPES = [
  "backlog",
  "directive",
  "build-complete",
  "finding",
  "pre-flight",
  "blocker",
  "sprint-planned",
  "interview",
  "prd-draft",
  "prd-update",
  "feature-card",
  "acceptance-criteria",
  "test-case",
  "test-result",
  "track-backlog",
];

export function needsProjectTagWarning(tags: string[]): boolean {
  return tags.some((t) => PROJECT_TAG_REQUIRED_TYPES.includes(t)) && !tags.some((t) => t.startsWith("project:"));
}

// Firestore collection both the server (Admin SDK) and the client (browser
// SDK, ERComposerV1.tsx's direct-write path) increment — a flat top-level
// collection with a composite {workspaceId}_{author} doc ID, same shape as
// the existing relay_rate_limits pattern, rather than a relay_workspaces
// subcollection (avoids needing a second Firestore rule match block).
export const PROJECT_TAG_WARNINGS_COLLECTION = "relay_project_tag_warnings";

export function projectTagWarningDocId(workspaceId: string, authorId: string): string {
  return `${workspaceId}_${authorId}`;
}

// Per-author (not pooled across the team) count of missed-tag posts before
// the author sees the upgraded warning. Below this, behavior is unchanged:
// the post succeeds, gets project:unscoped appended (server-side only —
// ERComposerV1.tsx's direct-write path does not currently apply this),
// logged server-side. Deliberately never a hard rejection at any count —
// "we can't stop them from messing up... but we can warn them."
export const PROJECT_TAG_WARNING_THRESHOLD = 25;

export const PROJECT_TAG_WARNING_MESSAGE =
  "Team Log posts without a project tag are hard to find later and make it difficult to track your project's work. " +
  "To create a project: click New Project in the left nav, then tag your posts with project:<name>. Your agents will pick up and use the same tag automatically.";

// HTTP header values must be Latin-1 at most and free of control characters;
// Node throws ERR_INVALID_CHAR otherwise (an em dash in the warning text once
// turned a stored post into a 500). Anything outside printable ASCII becomes
// a space. Astral characters (emoji) are two UTF-16 units, hence "+".
export function toHeaderSafe(text: string): string {
  return text.replace(/[^\x20-\x7E]+/g, " ");
}
